import React, { useEffect, useState } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import { Card, Row, Col, Button } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';

import {
  listarGruposEmpresaTransferenciaOcorrencias,
  listarContasParaTransferenciaOcorrencias,
  contarOcorrenciasConta,
  transferirOcorrencias,
} from '~/Services/Service';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';

import ModalConfirmacao from './ModalConfirmacao';
import { validationSchema } from './validationSchema';

const INITIAL_VALUES = {
  grupo_empresa: '',
  conta_origem: '',
  conta_destino: '',
};

// Sub-componente que reage à mudança do GE para recarregar contas.
// Mantém os hooks no topo do próprio componente (rules-of-hooks).
const CarregadorContas = ({ onContasChange }) => {
  const { values, setFieldValue } = useFormikContext();
  const geSelecionado = values.grupo_empresa;

  useEffect(() => {
    if (!geSelecionado) {
      onContasChange([]);
      return undefined;
    }
    let ativo = true;
    listarContasParaTransferenciaOcorrencias(geSelecionado)
      .then((resp) => {
        if (!ativo) return;
        const lista = Array.isArray(resp) ? resp : [];
        onContasChange(lista);
        setFieldValue('conta_origem', '');
        setFieldValue('conta_destino', '');
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(
          extrairMensagemErro(err) || 'Falha ao carregar contas do GE.'
        );
        onContasChange([]);
      });
    return () => {
      ativo = false;
    };
    // setFieldValue e onContasChange são estáveis; geSelecionado dispara o reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geSelecionado]);

  return null;
};

const TransferenciaOcorrencias = () => {
  const [ges, setGes] = useState([]);
  const [contas, setContas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [dadosConfirmacao, setDadosConfirmacao] = useState(null);
  const [loadingTransferencia, setLoadingTransferencia] = useState(false);

  useEffect(() => {
    let ativo = true;
    listarGruposEmpresaTransferenciaOcorrencias()
      .then((resp) => {
        if (!ativo) return;
        setGes(Array.isArray(resp) ? resp : []);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(
          extrairMensagemErro(err) || 'Falha ao carregar Grupos de Empresa.'
        );
      });
    return () => {
      ativo = false;
    };
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const resp = await contarOcorrenciasConta(
        values.conta_origem,
        values.grupo_empresa
      );
      const quantidade = Array.isArray(resp)
        ? resp.length
        : resp && typeof resp.length === 'number'
        ? resp.length
        : 0;

      if (quantidade === 0) {
        toast.error('A Conta Origem não possui ocorrências para transferir.');
        setSubmitting(false);
        return;
      }

      const ge = ges.find((g) => g.codigo === values.grupo_empresa);
      const origem = contas.find(
        (c) => c.codigo_conta === values.conta_origem
      );
      const destino = contas.find(
        (c) => c.codigo_conta === values.conta_destino
      );

      setDadosConfirmacao({
        quantidade,
        apelido_ge: ge && ge.apelido ? ge.apelido : '',
        codigo_origem: origem ? origem.codigo_conta : values.conta_origem,
        apelido_origem: origem && origem.apelido_conta ? origem.apelido_conta : '',
        codigo_destino: destino ? destino.codigo_conta : values.conta_destino,
        apelido_destino:
          destino && destino.apelido_conta ? destino.apelido_conta : '',
        values,
      });
      setShowModal(true);
    } catch (err) {
      toast.error(extrairMensagemErro(err) || 'Falha na pré-validação.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmar = async (resetForm) => {
    if (!dadosConfirmacao) return;
    setLoadingTransferencia(true);
    try {
      const {
        values,
        apelido_origem,
        apelido_destino,
        quantidade,
      } = dadosConfirmacao;
      const payload = {
        grupo_empresa: values.grupo_empresa,
        conta_origem: values.conta_origem,
        conta_destino: values.conta_destino,
        apelido_origem,
        apelido_destino,
      };
      const resp = await transferirOcorrencias(payload);
      const movidas =
        resp && typeof resp.ocorrencias_transferidas === 'number'
          ? resp.ocorrencias_transferidas
          : quantidade;
      toast.success(
        `Transferência concluída. ${movidas} ocorrência(s) movida(s).`
      );
      setShowModal(false);
      setDadosConfirmacao(null);
      resetForm();
    } catch (err) {
      toast.error(
        extrairMensagemErro(err) ||
          'Falha na transferência. Tente novamente ou contate o suporte.'
      );
    } finally {
      setLoadingTransferencia(false);
    }
  };

  const opcoesGe = ges.map((g) => ({
    value: g.codigo,
    label: g.apelido ? `${g.codigo} - ${g.apelido}` : `${g.codigo}`,
  }));

  const opcoesContas = contas.map((c) => ({
    value: c.codigo_conta,
    label: `${c.codigo_conta} - ${c.apelido_conta || ''}`.trim(),
  }));

  return (
    <Card>
      <Card.Header>
        <h5 className="mb-0">Transferência de Ocorrências</h5>
      </Card.Header>
      <Card.Body>
        <Formik
          initialValues={INITIAL_VALUES}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            isSubmitting,
            resetForm,
          }) => (
            <Form>
              <CarregadorContas onContasChange={setContas} />

              <Row>
                <Col md={6}>
                  <label className="small mb-1">Grupo de Empresa *</label>
                  <Select
                    key={`ge-${values.grupo_empresa || 'vazio'}`}
                    options={opcoesGe}
                    value={
                      opcoesGe.find((o) => o.value === values.grupo_empresa) ||
                      null
                    }
                    onChange={(opt) =>
                      setFieldValue('grupo_empresa', opt ? opt.value : '')
                    }
                    placeholder="Selecione o GE"
                    styles={selectStylesSm}
                    isClearable
                  />
                  {touched.grupo_empresa && errors.grupo_empresa && (
                    <small className="text-danger">
                      {errors.grupo_empresa}
                    </small>
                  )}
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={6}>
                  <label className="small mb-1">Conta Origem *</label>
                  <Select
                    key={`origem-${values.grupo_empresa || 'vazio'}-${
                      values.conta_origem || 'vazio'
                    }`}
                    options={opcoesContas}
                    value={
                      opcoesContas.find(
                        (o) => o.value === values.conta_origem
                      ) || null
                    }
                    onChange={(opt) =>
                      setFieldValue('conta_origem', opt ? opt.value : '')
                    }
                    placeholder="Selecione a Conta Origem"
                    styles={selectStylesSm}
                    isDisabled={!values.grupo_empresa}
                    isClearable
                  />
                  {touched.conta_origem && errors.conta_origem && (
                    <small className="text-danger">{errors.conta_origem}</small>
                  )}
                </Col>
                <Col md={6}>
                  <label className="small mb-1">Conta Destino *</label>
                  <Select
                    key={`destino-${values.grupo_empresa || 'vazio'}-${
                      values.conta_destino || 'vazio'
                    }`}
                    options={opcoesContas}
                    value={
                      opcoesContas.find(
                        (o) => o.value === values.conta_destino
                      ) || null
                    }
                    onChange={(opt) =>
                      setFieldValue('conta_destino', opt ? opt.value : '')
                    }
                    placeholder="Selecione a Conta Destino"
                    styles={selectStylesSm}
                    isDisabled={!values.grupo_empresa}
                    isClearable
                  />
                  {touched.conta_destino && errors.conta_destino && (
                    <small className="text-danger">
                      {errors.conta_destino}
                    </small>
                  )}
                </Col>
              </Row>

              <Row className="mt-4">
                <Col className="d-flex justify-content-end">
                  <Button
                    variant="outline-secondary"
                    className="mr-2"
                    onClick={() => resetForm()}
                    disabled={isSubmitting || loadingTransferencia}
                  >
                    Limpar
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting || loadingTransferencia}
                  >
                    {isSubmitting ? 'Validando...' : 'Transferir'}
                  </Button>
                </Col>
              </Row>

              <ModalConfirmacao
                show={showModal}
                onHide={() => {
                  setShowModal(false);
                  setDadosConfirmacao(null);
                }}
                onConfirm={() => handleConfirmar(resetForm)}
                dados={dadosConfirmacao}
                loading={loadingTransferencia}
              />
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default TransferenciaOcorrencias;
